import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api } from '@/api/client';

export type BranchOption = {
  id: string;
  code: string;
  name: string;
  country?: string;
  city?: string;
  is_head_office?: boolean;
  branch_type?: string;
};

export type SubscriptionAccess = {
  allowed:boolean;
  status:string;
  licensedUsers:number;
  maxBranches:number|null;
  currentPeriodEnd:string|null;
  trialEndsAt:string|null;
  daysRemaining:number|null;
  renewalWarning:boolean;
  planName:string|null;
};

export type LoginContext = 'workspace' | 'platform' | 'driver';
export type WorkspaceTheme = { primaryColor:string; accentColor:string };

export type User = {
  id: string;
  name: string;
  roles: string[];
  company: string;
  workspace: string;
  email?: string;
  branchId?: string | null;
  branchIds: string[];
  allBranches: boolean;
  branches: BranchOption[];
};

export type LoginResult = {
  token: string;
  workspace: string;
  loginContext: LoginContext;
  user: User;
  subscription: SubscriptionAccess;
  theme?: WorkspaceTheme;
  managedByPlatform?: boolean;
  platformActorName?: string | null;
};

type PlatformSessionSnapshot = {
  token:string;
  workspace:string;
  loginContext:LoginContext;
  user:User;
  subscription:SubscriptionAccess|null;
  theme:WorkspaceTheme;
  activeBranchId:string;
};

const DEFAULT_THEME:WorkspaceTheme={primaryColor:'#08293B',accentColor:'#F5A20B'};
const PLATFORM_SNAPSHOT_KEY='pivot_platform_session_snapshot';

function storedUser(): User | null {
  try { return JSON.parse(localStorage.getItem('pivot_user') ?? 'null') as User | null; }
  catch { return null; }
}

function storedTheme():WorkspaceTheme {
  try {
    const value=JSON.parse(localStorage.getItem('pivot_workspace_theme')??'null') as Partial<WorkspaceTheme>|null;
    return normaliseTheme(value);
  } catch { return {...DEFAULT_THEME}; }
}

function storedLoginContext(): LoginContext {
  const value = localStorage.getItem('pivot_login_context');
  return value === 'platform' ? 'platform' : value === 'driver' ? 'driver' : 'workspace';
}

function normaliseTheme(value?:Partial<WorkspaceTheme>|null):WorkspaceTheme {
  const valid=(colour:unknown,fallback:string)=>typeof colour==='string'&&/^#[0-9a-f]{6}$/i.test(colour)?colour:fallback;
  return {primaryColor:valid(value?.primaryColor,DEFAULT_THEME.primaryColor),accentColor:valid(value?.accentColor,DEFAULT_THEME.accentColor)};
}

function applyTheme(value:WorkspaceTheme):void {
  document.documentElement.style.setProperty('--workspace-primary',value.primaryColor);
  document.documentElement.style.setProperty('--workspace-accent',value.accentColor);
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(storedUser());
  const token = ref<string | null>(localStorage.getItem('pivot_token'));
  const loginContext = ref<LoginContext>(storedLoginContext());
  const verified = ref(false);
  const subscription = ref<SubscriptionAccess | null>(null);
  const theme=ref<WorkspaceTheme>(storedTheme());
  const managedByPlatform=ref(localStorage.getItem('pivot_managed_by_platform')==='true');
  const platformActorName=ref(localStorage.getItem('pivot_platform_actor_name')??'');
  const activeBranchId = ref(localStorage.getItem('pivot_branch_id') ?? user.value?.branchId ?? 'all');
  applyTheme(theme.value);

  const authenticated = computed(() => Boolean(token.value && user.value));
  const canSeeAllBranches = computed(() => Boolean(user.value?.allBranches));
  const branches = computed(() => user.value?.branches ?? []);
  const activeBranch = computed(() => branches.value.find((branch) => branch.id === activeBranchId.value) ?? null);
  const isManagedWorkspaceSession=computed(()=>managedByPlatform.value&&loginContext.value==='workspace');
  const isPlatformAdminSession = computed(() => loginContext.value === 'platform'
    && Boolean(user.value?.roles.some((role) => ['SUPER_ADMIN','PLATFORM_ADMIN','PLATFORM_BILLING','PLATFORM_SUPPORT','PLATFORM_OPERATIONS','PLATFORM_AUDITOR'].includes(role))));

  function normaliseBranchSelection(nextUser: User): void {
    const permitted = nextUser.allBranches || nextUser.branchIds.includes(activeBranchId.value);
    if (!permitted || (activeBranchId.value === 'all' && !nextUser.allBranches)) {
      activeBranchId.value = nextUser.branchId ?? nextUser.branchIds[0] ?? (nextUser.allBranches ? 'all' : '');
    }
    localStorage.setItem('pivot_branch_id', activeBranchId.value || 'all');
  }

  function persistTheme(value?:Partial<WorkspaceTheme>|null):void {
    theme.value=normaliseTheme(value);
    localStorage.setItem('pivot_workspace_theme',JSON.stringify(theme.value));
    applyTheme(theme.value);
  }

  function saveSession(result: LoginResult, fallbackWorkspace: string): void {
    token.value = result.token;
    user.value = result.user;
    subscription.value = result.subscription;
    loginContext.value = result.loginContext;
    managedByPlatform.value=Boolean(result.managedByPlatform);
    platformActorName.value=result.platformActorName??'';
    normaliseBranchSelection(result.user);
    persistTheme(result.theme);
    verified.value = true;
    localStorage.setItem('pivot_token', result.token);
    localStorage.setItem('pivot_login_context', result.loginContext);
    localStorage.setItem('pivot_workspace', result.workspace ?? fallbackWorkspace);
    localStorage.setItem('pivot_user', JSON.stringify(result.user));
    localStorage.setItem('pivot_managed_by_platform',String(managedByPlatform.value));
    if(platformActorName.value)localStorage.setItem('pivot_platform_actor_name',platformActorName.value);else localStorage.removeItem('pivot_platform_actor_name');
  }

  async function login(workspace: string, email: string, password: string): Promise<void> {
    sessionStorage.removeItem(PLATFORM_SNAPSHOT_KEY);
    const normalisedWorkspace = workspace.trim().toLowerCase();
    const result = await api.post<LoginResult>('/auth/login', { email, password, tenant: normalisedWorkspace });
    saveSession(result, normalisedWorkspace);
  }

  async function driverLogin(workspace: string, email: string, password: string): Promise<void> {
    sessionStorage.removeItem(PLATFORM_SNAPSHOT_KEY);
    const normalisedWorkspace = workspace.trim().toLowerCase();
    const result = await api.post<LoginResult>('/auth/driver/login', { email, password, tenant: normalisedWorkspace });
    saveSession(result, normalisedWorkspace);
  }

  async function saasAdminLogin(email: string, password: string): Promise<void> {
    sessionStorage.removeItem(PLATFORM_SNAPSHOT_KEY);
    const result = await api.post<LoginResult>('/auth/saas-admin/login', { email, password });
    saveSession(result, 'pivot-yard');
  }

  function enterManagedWorkspace(result:LoginResult):void {
    if(!token.value||!user.value||loginContext.value!=='platform')throw new Error('A valid SaaS administrator session is required.');
    const snapshot:PlatformSessionSnapshot={
      token:token.value,workspace:localStorage.getItem('pivot_workspace')??'pivot-yard',loginContext:loginContext.value,user:user.value,
      subscription:subscription.value,theme:theme.value,activeBranchId:activeBranchId.value
    };
    sessionStorage.setItem(PLATFORM_SNAPSHOT_KEY,JSON.stringify(snapshot));
    saveSession({...result,managedByPlatform:true},result.workspace);
  }

  function exitManagedWorkspace():boolean {
    try {
      const raw=sessionStorage.getItem(PLATFORM_SNAPSHOT_KEY);
      if(!raw)return false;
      const snapshot=JSON.parse(raw) as PlatformSessionSnapshot;
      token.value=snapshot.token;user.value=snapshot.user;subscription.value=snapshot.subscription;loginContext.value=snapshot.loginContext;
      activeBranchId.value=snapshot.activeBranchId;managedByPlatform.value=false;platformActorName.value='';verified.value=false;
      localStorage.setItem('pivot_token',snapshot.token);localStorage.setItem('pivot_user',JSON.stringify(snapshot.user));
      localStorage.setItem('pivot_login_context',snapshot.loginContext);localStorage.setItem('pivot_workspace',snapshot.workspace);
      localStorage.setItem('pivot_branch_id',snapshot.activeBranchId);localStorage.setItem('pivot_managed_by_platform','false');
      localStorage.removeItem('pivot_platform_actor_name');persistTheme(snapshot.theme);sessionStorage.removeItem(PLATFORM_SNAPSHOT_KEY);
      return true;
    } catch { sessionStorage.removeItem(PLATFORM_SNAPSHOT_KEY);return false; }
  }

  async function verify(force = false): Promise<boolean> {
    if (!token.value || !user.value) { logout(); return false; }
    if (verified.value && !force) return true;
    try {
      const result = await api.get<{ user: User; subscription: SubscriptionAccess; loginContext: LoginContext }>('/auth/me') as {user:User;subscription:SubscriptionAccess;loginContext:LoginContext;theme?:WorkspaceTheme;managedByPlatform?:boolean;platformActorName?:string|null};
      if (!result?.user) { logout(); return false; }
      user.value = result.user;
      subscription.value = result.subscription;
      loginContext.value = result.loginContext === 'platform' ? 'platform' : result.loginContext === 'driver' ? 'driver' : 'workspace';
      managedByPlatform.value=Boolean(result.managedByPlatform);
      platformActorName.value=result.platformActorName??platformActorName.value;
      normaliseBranchSelection(result.user);persistTheme(result.theme);
      localStorage.setItem('pivot_user', JSON.stringify(result.user));
      localStorage.setItem('pivot_login_context', loginContext.value);
      localStorage.setItem('pivot_managed_by_platform',String(managedByPlatform.value));
      if(platformActorName.value)localStorage.setItem('pivot_platform_actor_name',platformActorName.value);
      verified.value = true;
      return true;
    } catch { logout(); return false; }
  }

  function setActiveBranch(branchId: string): void {
    if (!user.value) return;
    if (branchId === 'all' && !user.value.allBranches) return;
    if (branchId !== 'all' && !user.value.allBranches && !user.value.branchIds.includes(branchId)) return;
    activeBranchId.value = branchId;
    localStorage.setItem('pivot_branch_id', branchId);
    window.dispatchEvent(new CustomEvent('pivot:branch-changed', { detail: { branchId } }));
  }

  function logout(): void {
    token.value = null;user.value = null;verified.value = false;subscription.value = null;loginContext.value = 'workspace';
    managedByPlatform.value=false;platformActorName.value='';activeBranchId.value='all';persistTheme(DEFAULT_THEME);
    localStorage.removeItem('pivot_token');localStorage.removeItem('pivot_user');localStorage.removeItem('pivot_branch_id');
    localStorage.removeItem('pivot_login_context');localStorage.removeItem('pivot_workspace');localStorage.removeItem('pivot_managed_by_platform');
    localStorage.removeItem('pivot_platform_actor_name');sessionStorage.removeItem(PLATFORM_SNAPSHOT_KEY);
  }

  window.addEventListener('pivot:unauthorized', logout);
  window.addEventListener('pivot:subscription-expired', logout);
  return {
    user,token,verified,subscription,theme,loginContext,managedByPlatform,platformActorName,authenticated,activeBranchId,activeBranch,branches,
    canSeeAllBranches,isPlatformAdminSession,isManagedWorkspaceSession,login,driverLogin,saasAdminLogin,enterManagedWorkspace,exitManagedWorkspace,verify,setActiveBranch,setTheme:persistTheme,logout
  };
});
